import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * =============================================================================
 *  fetch + ReadableStream 的 SSE。
 *
 *  原生 EventSource 只會 GET、帶不了自訂 header——而變更型端點一律要求
 *  `X-Requested-With`（deps.require_fetch_header 的 CSRF 那一列），iOS App 的每個請求
 *  還要帶 `Authorization: Bearer`。所以串流改由 typed client（openapi-fetch，
 *  `parseAs: 'stream'`，baseUrl / 憑證由 lib/api/client 統一決定）發出，本檔只負責兩件事：
 *
 *    1. `createSseParser` / `readSseStream`：依 WHATWG「event stream interpretation」
 *       把位元組切成事件。frame 可能被切在任何一個位元組上（包括 CRLF 的中間、
 *       多位元組 UTF-8 字元的中間），也可能一次讀到好幾個 frame。
 *    2. `useFetchSse`：一個 AbortController 的生命週期——開新請求時取消舊的、
 *       呼叫端關閉時取消、元件卸載時取消。
 * ========================================================================== */

export interface SseMessage {
  /** `event:` 欄位；沒有時依規格為 `message`。 */
  event: string;
  /** 多行 `data:` 以 `\n` 串接後的內容。 */
  data: string;
  /** 最後一次看到的 `id:`（規格上的 last event ID）。 */
  id: string;
}

export interface SseParser {
  /** 餵入已解碼的文字片段（任意切法）。 */
  feed(chunk: string): void;
  /** 串流結束。規格要求丟棄最後一個沒有以空行結尾的事件。 */
  end(): void;
}

export function createSseParser(onMessage: (message: SseMessage) => void): SseParser {
  let buffer = '';
  let eventType = '';
  let data = '';
  let lastId = '';
  let sawData = false;

  const dispatch = () => {
    if (sawData) {
      onMessage({ event: eventType === '' ? 'message' : eventType, data, id: lastId });
    }
    eventType = '';
    data = '';
    sawData = false;
  };

  const processLine = (line: string) => {
    if (line === '') {
      dispatch();
      return;
    }
    if (line.startsWith(':')) return; // 註解（心跳）
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    switch (field) {
      case 'event':
        eventType = value;
        break;
      case 'data':
        data = sawData ? `${data}\n${value}` : value;
        sawData = true;
        break;
      case 'id':
        if (!value.includes('\0')) lastId = value;
        break;
      default:
        // `retry` 與未知欄位：單次請求用不到重連，忽略。
        break;
    }
  };

  return {
    feed(chunk) {
      buffer += chunk;
      let start = 0;
      for (let i = 0; i < buffer.length; i++) {
        const ch = buffer[i];
        if (ch === '\n') {
          processLine(buffer.slice(start, i));
          start = i + 1;
        } else if (ch === '\r') {
          // CR 在片段最後一個字元：可能是被切開的 CRLF，等下一片再決定。
          if (i === buffer.length - 1) break;
          processLine(buffer.slice(start, i));
          if (buffer[i + 1] === '\n') i++;
          start = i + 1;
        }
      }
      buffer = buffer.slice(start);
    },
    end() {
      // 以單獨 CR 結尾的最後一行仍是一個完整的行。
      if (buffer.endsWith('\r')) processLine(buffer.slice(0, -1));
      buffer = '';
      eventType = '';
      data = '';
      sawData = false;
    },
  };
}

/**
 * 把一條位元組串流讀完，逐個事件回呼。`signal` 中止時取消讀取並以 AbortError 結束。
 */
export async function readSseStream(
  stream: ReadableStream<Uint8Array>,
  onMessage: (message: SseMessage) => void,
  signal?: AbortSignal,
): Promise<void> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  const parser = createSseParser(onMessage);
  const onAbort = () => {
    void reader.cancel().catch(() => undefined);
  };
  signal?.addEventListener('abort', onAbort, { once: true });
  try {
    for (;;) {
      signal?.throwIfAborted();
      const { done, value } = await reader.read();
      if (done) break;
      parser.feed(decoder.decode(value, { stream: true }));
    }
    signal?.throwIfAborted();
    parser.feed(decoder.decode());
    parser.end();
  } finally {
    signal?.removeEventListener('abort', onAbort);
    reader.releaseLock();
  }
}

export interface FetchSseHandle {
  /**
   * 以一個新的 AbortSignal 執行 `task`（通常是「發請求 → readSseStream」）。
   * 進行中的前一個 task 會先被取消。被取消的 task 回傳 `{ aborted: true }`，不 throw。
   */
  run<T>(task: (signal: AbortSignal) => Promise<T>): Promise<{ aborted: true } | { aborted: false; value: T }>;
  /** 取消進行中的 task（沒有就什麼都不做）。 */
  abort(): void;
  /** 是否有 task 進行中。 */
  active: boolean;
}

export function useFetchSse(): FetchSseHandle {
  const controllerRef = useRef<AbortController | null>(null);
  const [active, setActive] = useState(false);

  useEffect(
    () => () => {
      controllerRef.current?.abort();
      controllerRef.current = null;
    },
    [],
  );

  const abort = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setActive(false);
  }, []);

  const run = useCallback(async <T,>(task: (signal: AbortSignal) => Promise<T>) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setActive(true);
    try {
      const value = await task(controller.signal);
      if (controller.signal.aborted) return { aborted: true } as const;
      return { aborted: false, value } as const;
    } catch (error) {
      if (controller.signal.aborted) return { aborted: true } as const;
      throw error;
    } finally {
      if (controllerRef.current === controller) {
        controllerRef.current = null;
        setActive(false);
      }
    }
  }, []);

  return { run, abort, active };
}
